import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, ArrowLeft, Save, RotateCcw, Settings, 
  Zap, Target, Clock, BarChart3, Info
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const AISmartAssignment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules/smart_assignment`, {
        params: { token }
      });
      
      if (response.data.success) {
        setConfig(response.data.module);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data.module)));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/api/ai-intelligence/modules/smart_assignment?token=${token}`,
        {
          enabled: config.enabled,
          settings: config.settings
        }
      );
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        toast.success('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      await axios.post(`${API}/api/ai-intelligence/modules/smart_assignment/reset?token=${token}`);
      toast.success('Reset to defaults');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="smart-assignment-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/ai-intelligence')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
              <Users className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Smart Lead Assignment</h1>
              <p className="text-sm text-slate-500">AI-powered agent recommendation engine</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Unsaved Changes
            </Badge>
          )}
          <Switch
            checked={config?.enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            data-testid="module-toggle"
          />
          <span className={`text-sm font-medium ${config?.enabled ? 'text-green-600' : 'text-slate-500'}`}>
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {!config?.enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This module is currently disabled. Enable it to use smart lead assignment features.
          </AlertDescription>
        </Alert>
      )}

      {/* Mode Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Assignment Mode
          </CardTitle>
          <CardDescription>
            Choose how the AI should handle lead assignments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.mode === 'recommendation'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('mode', 'recommendation')}
              data-testid="mode-recommendation"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  config?.settings?.mode === 'recommendation' ? 'bg-blue-500' : 'bg-slate-200'
                }`}>
                  <Zap className={`h-4 w-4 ${
                    config?.settings?.mode === 'recommendation' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <span className="font-medium">Recommendation Only</span>
              </div>
              <p className="text-sm text-slate-500">
                AI suggests the best agent, but assignment is done manually
              </p>
            </div>
            
            <div
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                config?.settings?.mode === 'auto_assign'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onClick={() => updateSetting('mode', 'auto_assign')}
              data-testid="mode-auto-assign"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${
                  config?.settings?.mode === 'auto_assign' ? 'bg-blue-500' : 'bg-slate-200'
                }`}>
                  <Target className={`h-4 w-4 ${
                    config?.settings?.mode === 'auto_assign' ? 'text-white' : 'text-slate-600'
                  }`} />
                </div>
                <span className="font-medium">Auto Assign</span>
              </div>
              <p className="text-sm text-slate-500">
                AI automatically assigns leads to the recommended agent
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Allow Manual Override</Label>
              <p className="text-sm text-slate-500">Admins can override AI assignments</p>
            </div>
            <Switch
              checked={config?.settings?.allow_manual_override}
              onCheckedChange={(checked) => updateSetting('allow_manual_override', checked)}
              data-testid="allow-override"
            />
          </div>
        </CardContent>
      </Card>

      {/* Assignment Strategy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Assignment Strategy
          </CardTitle>
          <CardDescription>
            Define how the AI selects the best agent for each lead
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="mb-2 block">Primary Strategy</Label>
            <Select
              value={config?.settings?.assignment_strategy}
              onValueChange={(value) => updateSetting('assignment_strategy', value)}
            >
              <SelectTrigger data-testid="strategy-select">
                <SelectValue placeholder="Select strategy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="best_conversion">Best Conversion Rate</SelectItem>
                <SelectItem value="best_response_time">Best Response Time</SelectItem>
                <SelectItem value="best_project">Best Project-Specific Performer</SelectItem>
                <SelectItem value="balanced">Balanced (Performance + Workload)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="mb-2 block">Minimum Historical Data</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={config?.settings?.min_historical_data || 10}
                  onChange={(e) => updateSetting('min_historical_data', parseInt(e.target.value))}
                  min={1}
                  max={100}
                  className="w-24"
                  data-testid="min-historical"
                />
                <span className="text-sm text-slate-500">leads required for AI analysis</span>
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Max Leads Per Agent</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  value={config?.settings?.max_leads_per_agent || 50}
                  onChange={(e) => updateSetting('max_leads_per_agent', parseInt(e.target.value))}
                  min={10}
                  max={200}
                  className="w-24"
                  data-testid="max-leads"
                />
                <span className="text-sm text-slate-500">active leads per agent</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Consider Agent Workload</Label>
              <p className="text-sm text-slate-500">Factor in current workload when assigning</p>
            </div>
            <Switch
              checked={config?.settings?.consider_workload}
              onCheckedChange={(checked) => updateSetting('consider_workload', checked)}
              data-testid="consider-workload"
            />
          </div>
        </CardContent>
      </Card>

      {/* Scoring Weights */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-blue-600" />
            Scoring Weights
          </CardTitle>
          <CardDescription>
            Adjust how different factors influence agent selection
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex justify-between mb-2">
              <Label>Source Performance Weight</Label>
              <span className="text-sm font-medium">{config?.settings?.source_weight || 30}%</span>
            </div>
            <Slider
              value={[config?.settings?.source_weight || 30]}
              onValueChange={([value]) => updateSetting('source_weight', value)}
              max={50}
              min={0}
              step={5}
              data-testid="source-weight"
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Project Expertise Weight</Label>
              <span className="text-sm font-medium">{config?.settings?.project_weight || 25}%</span>
            </div>
            <Slider
              value={[config?.settings?.project_weight || 25]}
              onValueChange={([value]) => updateSetting('project_weight', value)}
              max={50}
              min={0}
              step={5}
              data-testid="project-weight"
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Overall Performance Weight</Label>
              <span className="text-sm font-medium">{config?.settings?.performance_weight || 30}%</span>
            </div>
            <Slider
              value={[config?.settings?.performance_weight || 30]}
              onValueChange={([value]) => updateSetting('performance_weight', value)}
              max={50}
              min={0}
              step={5}
              data-testid="performance-weight"
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Availability Weight</Label>
              <span className="text-sm font-medium">{config?.settings?.availability_weight || 15}%</span>
            </div>
            <Slider
              value={[config?.settings?.availability_weight || 15]}
              onValueChange={([value]) => updateSetting('availability_weight', value)}
              max={50}
              min={0}
              step={5}
              data-testid="availability-weight"
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-slate-600"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AISmartAssignment;
